// QR Code Service - GraphQL operations for QR Codes
import { graphqlClient } from '../graphqlClient';

const getRestBaseUrl = () => {
  const graphUrl = graphqlClient?.apiUrl || '';

  if (!graphUrl || graphUrl === '/graphql') {
    return '';
  }

  return graphUrl.endsWith('/graphql')
    ? graphUrl.slice(0, -'/graphql'.length)
    : graphUrl;
};

export const createMerchantQRCode = async ({ amount, reference }) => {
  const profileQuery = `
    query MerchantProfileForQrCreation {
      merchantProfile {
        secretKey
      }
    }
  `;

  const profileResult = await graphqlClient.query(profileQuery);
  const secretKey = profileResult?.merchantProfile?.secretKey;

  if (!secretKey) {
    throw new Error('Merchant secret key not found. Please contact admin.');
  }

  const amountNumber = Number(amount);
  const amountInCents = Math.round(amountNumber * 100);

  if (!Number.isFinite(amountInCents) || amountInCents < 1) {
    throw new Error('Invalid amount');
  }

  const payload = {
    amount: amountInCents,
    currency: 'THB',
    reference: reference?.trim() || undefined,
    metadata: {
      source: 'merchant_panel_deposit',
    },
  };

  const response = await fetch(`${getRestBaseUrl()}/v1/qr-codes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${secretKey}`,
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const message = responseBody?.message || 'Failed to create QR code';
    throw new Error(Array.isArray(message) ? message.join(', ') : message);
  }

  return responseBody;
};

// ==================== Merchant QR Codes ====================

export const getMerchantQRCodes = async () => {
  const query = `
    query GetMerchantQRCodes {
      getMerchantQRCodes {
        id
        qr_code_id
        qr_data
        amount
        currency
        reference
        client_name
        client_bank_name
        client_bank_account
        expires_at
        payment_id
        status
        metadata
        merchant_id
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getMerchantQRCodes || [];
};

export const getEngineQRCodes = async () => {
  const query = `
    query GetEngineQRCodes {
      getEngineQRCodes {
        id
        qr_code_id
        qr_data
        amount
        currency
        reference
        client_name
        client_bank_name
        client_bank_account
        expires_at
        payment_id
        status
        metadata
        merchant_id
        merchant {
          id
          code
          name
        }
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getEngineQRCodes || [];
};


// ================== Merchant QR codes by id ====================

export const getQRCodesByMerchantId = async (merchantId) => {
  const query = `
    query GetQRCodesByMerchantId($merchantId: ID!) {
      getQRCodesByMerchantId(merchantId: $merchantId) {
        id
        qr_code_id
        qr_data
        amount
        currency
        reference
        client_name
        client_bank_name
        client_bank_account
        expires_at
        payment_id
        status
        metadata
        merchant_id
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { merchantId });
  return result.getQRCodesByMerchantId || [];
};

export const getMerchantQRCodesPagination = async (page = 1, limit = 10, search = "", filters = null, merchants = null) => {
  const query = `
    query MerchantQRCodes($page: Int, $limit: Int, $search: String, $filters: QRCodeFilterInput, $merchants: [String]) {
      merchantQRCodes(page: $page, limit: $limit, search: $search, filters: $filters, merchants: $merchants) {
        data {
          id
          qr_code_id
          qr_data
          amount
          net_amount
          total_fee
          currency
          reference
          client_name
          client_bank_name
          client_bank_account
          expires_at
          payment_id
          status
          metadata
          merchant {
            id
            code
            name
          }
          createdAt
          updatedAt
        }
        total
        page
        limit
        totalPages
      }
    }
  `;

  const variables = {
    page,
    limit,
    search: search || undefined,
    filters: filters || undefined,
    merchants: merchants || undefined,
  };

  const result = await graphqlClient.query(query, variables);
  return result.merchantQRCodes || { data: [], total: 0, page: 1, limit: 10, totalPages: 0 };
};